import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Divider from 'src/components/ui/Divider/Divider';
import { $publicApi } from 'src/services/api';
import logger from 'src/utils/logger';
import { useRegistrationFormContext } from '../RegistrationFormContext';
import { extractBackendErrorMessage } from '../errorUtils';

interface ConfirmStepProps {
  registrationKey: string;
  onBack: () => void;
  goToStep: (step: number) => void;
}

const STEP_FOR_FIELD: Record<string, number> = {
  company_name: 1,
  company_tax_id: 1,
  company_bin: 1,
  address: 1,
  remission_address: 1,
  partner_name: 2,
  partner_email: 2,
  partner_phone: 2,
  partner_position: 2,
  logo: 2,
  id_document: 2,
  bank_account: 3,
  iban: 3,
  bic: 3,
  bank_name: 3,
  payment_terms: 3,
  term_ids: 3,
};

interface SummaryRowProps {
  label: string;
  value?: string | null;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ label, value }) =>
  value ? (
    <div className="flex justify-between gap-12 py-4">
      <span className="typo-text-14 text-faded">{label}</span>
      <span className="typo-text-14--bold text-full text-right">{value}</span>
    </div>
  ) : null;

const ConfirmStep: React.FC<ConfirmStepProps> = ({ registrationKey, onBack, goToStep }) => {
  const { t } = useTranslation();
  const { formState } = useRegistrationFormContext();
  const [submitted, setSubmitted] = useState(false);
  const [missingItems, setMissingItems] = useState<string[]>([]);
  const [genericError, setGenericError] = useState<string | null>(null);

  const { mutateAsync, isPending } = $publicApi.useMutation('post', '/registrations/{registration_key}');

  const { companyData, verification, bankAndTerms, acceptedTerms } = formState;

  const onSubmit = async () => {
    setMissingItems([]);
    setGenericError(null);
    try {
      await mutateAsync({
        params: { path: { registration_key: registrationKey } },
        body: { term_ids: acceptedTerms },
      });
      setSubmitted(true);
    } catch (err: any) {
      logger.error('Failed to confirm registration', err);
      const detail = err?.detail ?? err?.body?.detail;
      const missing = detail?.missing_fields ?? detail?.missing ?? null;
      if (Array.isArray(missing) && missing.length > 0) {
        setMissingItems(missing);
        return;
      }
      const backendMessage = extractBackendErrorMessage(err);
      const fallback = t('registration.confirm.generic_error');
      setGenericError(backendMessage || fallback);
      toast.error(backendMessage || fallback);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col gap-16">
        <h3 className="typo-title-24 text-full">{t('registration.confirm.submitted_title')}</h3>
        <p className="typo-text-16 text-faded">{t('registration.confirm.submitted_copy')}</p>
      </div>
    );
  }

  const addressLine = [
    companyData.address.street,
    companyData.address.street2,
    [companyData.address.zip_code, companyData.address.city].filter(Boolean).join(' '),
    companyData.address.country_name || companyData.address.country_code,
  ]
    .filter(Boolean)
    .join(', ');

  const remissionAddressLine = [
    companyData.remission_address.address.street,
    companyData.remission_address.address.street2,
    [companyData.remission_address.address.zip_code, companyData.remission_address.address.city]
      .filter(Boolean)
      .join(' '),
    companyData.remission_address.address.country_name || companyData.remission_address.address.country_code,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="flex flex-col gap-24">
      <h3 className="typo-title-24 text-full">{t('registration.steps.confirm')}</h3>

      <section className="border border-subtle rounded-8 p-16">
        <div className="flex justify-between items-center mb-8">
          <h4 className="typo-title-20 text-full">{t('registration.steps.company_data')}</h4>
          <Button title={t('general.edit')} tone={ButtonToneProps.SECONDARY} onClick={() => goToStep(1)} />
        </div>
        <SummaryRow label={t('registration.fields.company_name')} value={companyData.company_name} />
        <SummaryRow label={t('registration.fields.company_tax_id')} value={companyData.company_tax_id} />
        <SummaryRow label={t('registration.fields.company_bin')} value={companyData.company_bin} />
        <SummaryRow
          label={t('registration.fields.warehouse')}
          value={companyData.warehouse ? t(`registration.warehouse.${companyData.warehouse}`) : ''}
        />
        <SummaryRow label={t('registration.fields.address')} value={addressLine} />
        <SummaryRow label={t('registration.fields.remission_company')} value={companyData.remission_address.company} />
        <SummaryRow label={t('registration.fields.remission_address')} value={remissionAddressLine} />
      </section>

      <section className="border border-subtle rounded-8 p-16">
        <div className="flex justify-between items-center mb-8">
          <h4 className="typo-title-20 text-full">{t('registration.steps.verification')}</h4>
          <Button title={t('general.edit')} tone={ButtonToneProps.SECONDARY} onClick={() => goToStep(2)} />
        </div>
        <SummaryRow label={t('registration.fields.partner_name')} value={verification.partner_name} />
        <SummaryRow label={t('registration.fields.partner_position')} value={verification.partner_position} />
        <SummaryRow label={t('registration.fields.partner_email')} value={verification.partner_email} />
        <SummaryRow label={t('registration.fields.partner_phone')} value={verification.partner_phone} />
      </section>

      <section className="border border-subtle rounded-8 p-16">
        <div className="flex justify-between items-center mb-8">
          <h4 className="typo-title-20 text-full">{t('registration.steps.bank_and_terms')}</h4>
          <Button title={t('general.edit')} tone={ButtonToneProps.SECONDARY} onClick={() => goToStep(3)} />
        </div>
        <SummaryRow
          label={t('registration.fields.payment_terms')}
          value={t(`registration.payment_terms.${bankAndTerms.payment_terms}`)}
        />
        {bankAndTerms.payment_terms === 'debit' && (
          <>
            <SummaryRow label={t('registration.fields.bank_name')} value={bankAndTerms.bank_account.bank_name} />
            <SummaryRow label={t('registration.fields.iban')} value={bankAndTerms.bank_account.iban} />
            <SummaryRow label={t('registration.fields.bic')} value={bankAndTerms.bank_account.bic} />
          </>
        )}
        <Divider customColor="border-subtle" className="my-8" />
        <p className="typo-text-14--bold text-full mb-4">{t('registration.terms.accepted')}</p>
        <ul className="list-disc pl-20">
          {acceptedTerms.map(term => (
            <li key={term.id ?? term.name} className="typo-text-14 text-full">
              {term.name}
            </li>
          ))}
        </ul>
      </section>

      {missingItems.length > 0 && (
        <div className="border border-subtle rounded-8 p-16 status-error-full">
          <p className="typo-text-14--bold mb-8">{t('registration.confirm.missing_title')}</p>
          <ul className="list-disc pl-20">
            {missingItems.map(item => (
              <li key={item} className="typo-text-14 flex items-center gap-8">
                <span>{item}</span>
                {STEP_FOR_FIELD[item] !== undefined && (
                  <button type="button" className="underline" onClick={() => goToStep(STEP_FOR_FIELD[item])}>
                    {t('general.edit')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {genericError && <p className="typo-text-14 status-error-full">{genericError}</p>}

      <div className="flex justify-between pt-16">
        <Button title={t('registration.back')} tone={ButtonToneProps.SECONDARY} onClick={onBack} />
        <Button
          title={t('registration.confirm.submit')}
          tone={ButtonToneProps.PRIMARY_BLUE}
          onClick={onSubmit}
          disabled={isPending}
        />
      </div>
    </div>
  );
};

export default ConfirmStep;
